import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import {
  convertRectangePointToYolo,
  getScaledCoordinates,
} from '@/utils/ia.utils';
import {
  getCanvasContext,
  initializeCanvas,
  drawRectangle,
  drawPolygon,
} from '@/utils/canvas.utils';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { Rectangle } from '@/entities/canvas';
import { MODE_DRAW } from '@/utils/constant.utils';
import { twMerge } from 'tailwind-merge';
import { Spinner } from '@/components/Loading/Spinner';
import { ImageDimension } from '@/type/detection.types';
import {
  useBoxCoordinates,
  useDenormalizePolygonPoints,
  usePolygonPoints,
  usePolygonScalePoints,
} from '@/hooks/moderation.hooks';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';

type ModerationCanvasProps = {
  elementClass?: string;
  imgSrc: string;
  setElement?: Dispatch<TResultImageCoordinatesSchema>;
  setDmg?: (dmg: TResultImageCoordinatesSchema) => void;
  mode: string;
  initialElement?: TResultImageCoordinatesSchema;
  originalBoundingBox?: Array<number>;
  selectedResultObject?: TResultObjectSchema;
  imageClassName?: string;
  onImageLoading?: (loading: boolean) => void;
};

export const ModerationReportCanvas = ({
  elementClass,
  imgSrc,
  setElement,
  setDmg,
  mode,
  initialElement,
  originalBoundingBox,
  selectedResultObject,
  imageClassName,
  onImageLoading,
}: ModerationCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageDimension, setImageDimension] = useState<ImageDimension | null>(
    null,
  );
  const [startPoint, setStartPoint] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [polygonPoints, setPolygonPoints] = useState<
    { x: number; y: number }[]
  >([]);
  const [isPolygonClosed, setIsPolygonClosed] = useState(false);

  const isModerated = initialElement?.moderated;

  const { getCoordinates: getBoxCoordinates } = useBoxCoordinates({
    isModerated,
    element: initialElement,
    imageDimension,
  });

  const { getPoints: initialPolygonPoints } = usePolygonPoints({
    element: initialElement,
  });

  const { denormalizePoints: denormalizePolygonPoints } =
    useDenormalizePolygonPoints({
      originalBoundingBox,
      element: initialElement,
    });

  const { getScalePoints: getPolygonScalePoints } = usePolygonScalePoints({
    isModerated,
    polygonPoints: initialPolygonPoints,
    denormalizePolygonPoints,
    imageDimension,
  });

  useEffect(() => {
    onImageLoading?.(isImageLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageLoading]);

  const drawInitialElement = useCallback(() => {
    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;

    if (initialElement) {
      if (mode === MODE_DRAW.RECTANGLE) {
        const box = getBoxCoordinates();
        const rect = {
          startX: (box.x / 100) * canvas.width,
          startY: (box.y / 100) * canvas.height,
          width: (box.width / 100) * canvas.width,
          height: (box.height / 100) * canvas.height,
        };

        drawRectangle(
          ctx,
          rect.startX,
          rect.startY,
          rect.width,
          rect.height,
          'red',
        );
      }
      if (mode === MODE_DRAW.POLYGONE && initialPolygonPoints) {
        const scaledPoints = getPolygonScalePoints(canvas);
        setIsPolygonClosed(true);
        drawPolygon(ctx, scaledPoints, 'red', true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedResultObject,
    initialElement,
    mode,
    getBoxCoordinates,
    initialPolygonPoints,
    getPolygonScalePoints,
    setIsPolygonClosed,
  ]);

  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;

    // Set loading to true when imgSrc changes
    setIsImageLoading(true);

    img.onload = () => {
      initializeCanvas(canvasRef, imageRef);
      drawInitialElement();
      // Set loading to false when image has loaded and canvas is initialized
      setIsImageLoading(false);

      // Set width and height to actual image dimensions
      if (imageRef.current) {
        imageRef.current.width = img.naturalWidth;
        imageRef.current.height = img.naturalHeight;
        // Update image dimensions state
        setImageDimension({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      }
    };

    // Handle case where image is already cached
    if (img.complete) {
      initializeCanvas(canvasRef, imageRef);
      drawInitialElement();
      setIsImageLoading(false);

      // Set width and height to actual image dimensions for cached images
      img.width = img.naturalWidth;
      img.height = img.naturalHeight;
      // Update image dimensions state for cached images
      setImageDimension({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    }
  }, [imgSrc]);

  // Separate effect to handle initialElement changes
  useEffect(() => {
    const img = imageRef.current;
    if (img && img.complete) {
      // Image is already loaded, redraw immediately
      initializeCanvas(canvasRef, imageRef);
      drawInitialElement();
    }
  }, [initialElement, mode, drawInitialElement]);

  const clearCanvas = () => {
    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    initializeCanvas(canvasRef, imageRef);
  };

  useEffect(() => {
    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;
    drawPolygon(ctx, polygonPoints, 'red');
  }, [polygonPoints]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { x, y } = getScaledCoordinates(e.clientX, e.clientY, canvasRef);

    if (mode === MODE_DRAW.POLYGONE) {
      if (isPolygonClosed) {
        setPolygonPoints([]);
        setIsPolygonClosed(false);
        clearCanvas();
      }
      setPolygonPoints((prevPoints) => [...prevPoints, { x, y }]);
    } else if (mode === MODE_DRAW.RECTANGLE) {
      setStartPoint({ x, y });
      setIsDrawing(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (mode === MODE_DRAW.POLYGONE) return;
    if (!isDrawing) return;

    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;

    initializeCanvas(canvasRef, imageRef);

    const { x: currentX, y: currentY } = getScaledCoordinates(
      e.clientX,
      e.clientY,
      canvasRef,
    );

    const width = currentX - startPoint.x;
    const height = currentY - startPoint.y;
    drawRectangle(ctx, startPoint.x, startPoint.y, width, height, 'red');

    if (mode === MODE_DRAW.POLYGONE && polygonPoints.length > 0) {
      drawPolygon(ctx, polygonPoints, 'red');
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(false);
    const { x: endXRect, y: endYRect } = getScaledCoordinates(
      e.clientX,
      e.clientY,
      canvasRef,
    );
    const newRectangle: Rectangle = {
      startX: startPoint.x,
      startY: startPoint.y,
      endX: endXRect,
      endY: endYRect,
    };
    const yoloCoordinates = convertRectangePointToYolo(
      elementClass,
      newRectangle,
      null,
      canvasRef,
    );
    if (yoloCoordinates) {
      setElement(
        initialElement
          ? { ...yoloCoordinates, elNumber: initialElement.elNumber }
          : yoloCoordinates,
      );
    }
  };

  const handleDoubleClick = () => {
    if (mode === MODE_DRAW.RECTANGLE) return;

    const { canvas, ctx } = getCanvasContext(canvasRef) || {};
    if (!canvas || !ctx) return;

    if (polygonPoints.length > 2) {
      drawPolygon(ctx, polygonPoints, 'red', true);
      setIsPolygonClosed(true);
      const yoloCoordinates = convertRectangePointToYolo(
        elementClass,
        null,
        polygonPoints,
        canvasRef,
        false,
      );
      if (yoloCoordinates) {
        setDmg({
          ...yoloCoordinates,
          point: [...yoloCoordinates.point],
          isDamaged: true,
        });
      }
      setPolygonPoints([]);
    }
  };

  return (
    <div className="relative inline-block">
      <img
        ref={imageRef}
        src={imgSrc}
        alt="Overlay"
        className={twMerge(
          'w-full h-auto',
          imageClassName,
          isImageLoading && 'min-h-[50vh] opacity-0',
        )}
      />

      <canvas
        className={twMerge(
          'absolute top-0 left-0 cursor-crosshair',
          isImageLoading && 'opacity-0',
        )}
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      />

      {isImageLoading && <Spinner />}
    </div>
  );
};
